import { useState, useEffect, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import { ArrowRight, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  DepositListItem,
  DepositUIStatus,
  OrderCandidate,
  ParsedDeposit,
  RescueRevalidation,
} from '@/supabase/deposit/types/deposit';
import { isStaleDepositError, isSettledOrderDepositError } from '@/supabase/deposit/depositErrors';
import { isFullPaymentMatch, compareReferenceCandidates, toKstDateString } from '@/supabase/deposit/service/depositService';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ConfirmGuard } from './ConfirmGuard';
import { CandidatePicker } from './CandidatePicker';

interface DepositRowProps {
  item: DepositListItem;
  // 서버 큐의 거래 맥락이 바뀌면 stale picker/확정 상태만 폐기한다. 상세 펼침은 유지.
  queueVersion: number;
  // expectedModifiedAt: 크로스탭 stale 확정 방지(낙관적 동시성). 행이 들고 있던 match.modified_at을 넘긴다 —
  // 로드 후 다른 탭이 그 입금을 제외/복구/변경했으면 link RPC가 거부(stale). (codexloop 제외 R2 F2)
  onConfirm: (
    depositMatchId: number,
    order: OrderCandidate,
    expectedModifiedAt: string,
  ) => Promise<void>;
  onUndo: (
    depositMatchId: number,
    expectedTotal: number | null,
    expectedPrice: number | null,
    expectedShipOverride: number | null,
    // 배송설정 지문(스펙 §3.3): 확정 행 페이로드(confirmedOrder)에 로드 시 부착된 값. rescue 행은 null 고정(§3.6).
    settingsFingerprint: string | null,
    // 구제 undo 원복(스펙 §5): pre-undo confirmed_source==='deposits_rescue'인 확정 행에서만 주입.
    rescueRevert?: { orderId: number; presetAmount: number },
  ) => Promise<void>;
  // 미매칭 입금 수동연결: 입금일 윈도우 내 미지정 주문 후보 로더(페이지가 user.id 주입). 선택 시 onConfirm(=linkMatch) 경유.
  onLoadManualCandidates: (deposit: ParsedDeposit) => Promise<OrderCandidate[]>;
  // ✗(제외): 미확정 입금을 '주문 아님'으로 제외. 미주입이면 '제외' 버튼 미표시(예: 처리 완료 행).
  onReject?: (depositMatchId: number) => Promise<void>;
  // 제외 복구: '제외됨' 행을 다시 확인 대기로. 미주입이면 '복구' 버튼 미표시.
  onRestore?: (depositMatchId: number) => Promise<void>;
  // 딥링크 하이라이트 상태(Task 5): ?match={matchId} URL 파라미터 소비.
  highlighted?: boolean;
  // 참고 후보 재검증 실패 시(스펙 §6.2) 큐 재조회를 요청 — stale 참고 행이 최신 스냅샷으로
  // 재도출되게 한다(codexloop R1 P2). 미주입이면 기존처럼 picker만 열고 refresh 없음.
  onRequestRefresh?: () => void;
  // 배송비 구제(PR2, 스펙 §5): 카드 클릭 시 재검증(fresh 재판정). 유효하면 fresh 후보+완납액+버전 반환,
  // 탈락이면 null(제안 철회). amount_mismatch 행에만 주입(actionNeeded).
  onRescueRevalidate?: (matchId: number, orderId: number, presetAmount: number) => Promise<RescueRevalidation | null>;
  // 구제 확정 체인(제출 시 재검증 → Step 1 확정 → Step 2 배송비 CAS). Step 1 실패는 throw(기존 에러 UX),
  // 그 외 결과 문자열 반환. amount_mismatch 행에만 주입.
  onRescueConfirm?: (matchId: number, orderId: number, presetAmount: number) => Promise<'ok' | 'shipping-unrecorded' | 'withdrawn'>;
}

interface StatusMeta {
  rail: string;
  ring: string;
}

// 프리미엄 톤: 좌측 3px 레일 + 카드 ring. 파랑=확실(matched/금액일치), amber=선택필요,
// slate=못찾음/완료. 기존 상태색(주문 카드)과 별개 — 입금 큐 전용 팔레트.
const STATUS_META: Record<DepositUIStatus, StatusMeta> = {
  matched: { rail: 'bg-blue-500', ring: 'ring-slate-200' },
  amount_suggested: { rail: 'bg-blue-500', ring: 'ring-slate-200' },
  ambiguous: { rail: 'bg-amber-400', ring: 'ring-amber-200' },
  amount_mismatch: { rail: 'bg-amber-500', ring: 'ring-amber-200' },
  unmatched: { rail: 'bg-slate-300', ring: 'ring-slate-200' },
  excluded: { rail: 'bg-slate-300', ring: 'ring-slate-200' },
  confirmed: { rail: 'bg-slate-300', ring: 'ring-slate-200' },
};

const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;

// 입금 일시를 KST 기준 'YYYY.MM.DD HH:mm'로. UTC+9 시프트 후 getUTC*로 직접 추출
// (Intl의 hour12/locale 포맷 함정 회피).
function formatDepositDateTime(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const yyyy = kst.getUTCFullYear();
  const mm = String(kst.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(kst.getUTCDate()).padStart(2, '0');
  const hh = String(kst.getUTCHours()).padStart(2, '0');
  const mi = String(kst.getUTCMinutes()).padStart(2, '0');
  return `${yyyy}.${mm}.${dd} ${hh}:${mi}`;
}

// 액션 버튼 공통: 44px 터치타겟 + 키보드 focus-visible 링(이전 shadcn Button 계약 보존).
const ACTION_BASE =
  'inline-flex min-h-11 items-center justify-center whitespace-nowrap rounded-lg text-[13px] font-semibold ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-500';

type GuardState =
  | {
      mode: 'confirm'; order: OrderCandidate; title: string; description?: string;
      warning?: string; notice?: string;
      rows?: Array<{ label: string; value: string; strong?: boolean }>;
    }
  | {
      // 배송비 구제 확정(스펙 §5). confirm과 rows/notice 패턴은 공유하되 확정 체인이 달라 별도 모드.
      mode: 'rescue'; orderId: number; presetAmount: number; title: string; description?: string;
      notice: string; rows: Array<{ label: string; value: string; strong?: boolean }>;
    }
  | { mode: 'undo'; title: string }
  | { mode: 'reject'; title: string }
  | { mode: 'restore'; title: string }
  | null;

export function DepositRow({
  item,
  queueVersion,
  onConfirm,
  onUndo,
  onLoadManualCandidates,
  onReject,
  onRestore,
  highlighted,
  onRequestRefresh,
  onRescueRevalidate,
  onRescueConfirm,
}: DepositRowProps) {
  const { deposit, match, uiStatus, candidates, confirmedOrder } = item;
  const meta = STATUS_META[uiStatus];

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [guard, setGuard] = useState<GuardState>(null);
  const [loading, setLoading] = useState(false);
  // amount_mismatch 단건 fast-path 확정 중(freshness 재검증 fetch) — 버튼 비활성용.
  const [fastConfirming, setFastConfirming] = useState(false);
  // 주문 상세(품목 요약 + 상품비/배송비) 인라인 펼침. 정산서 이동 없이 그 자리서 확인.
  const [detailOpen, setDetailOpen] = useState(false);

  // 미매칭 수동연결 picker 상태(null=미로드). 행이 바뀌면(match.id) 전부 리셋.
  const [manualOpen, setManualOpen] = useState(false);
  const [manualCandidates, setManualCandidates] = useState<OrderCandidate[] | null>(null);
  const [manualQuery, setManualQuery] = useState('');
  const [manualLoading, setManualLoading] = useState(false);
  // 늦게 도착한 후보 로드 응답이 재오픈/취소/행교체 후 state를 덮어쓰지 않게(stale-write 방지).
  const manualReqRef = useRef(0);
  const manualInputRef = useRef<HTMLInputElement>(null);
  const previousQueueVersionRef = useRef(queueVersion);
  const wasInvalidatedWhileLoadingRef = useRef(false);

  useEffect(() => {
    setSelectedId(null);
    setManualOpen(false);
    setManualCandidates(null);
    setManualQuery('');
    setManualLoading(false);
    setFastConfirming(false);
    setDetailOpen(false);
    manualReqRef.current++;
  }, [item.match.id]);

  // 같은 match.id라도 refresh로 uiStatus가 unmatched를 벗어나면(예: 후보주문이 다른 곳에 배정→재매칭)
  // 열려있던 manual picker/선택을 닫고 무효화(잔존 선택이 되살아나는 것 방지, R2 H3).
  useEffect(() => {
    if (uiStatus !== 'unmatched' && uiStatus !== 'amount_mismatch' && uiStatus !== 'ambiguous') {
      setManualOpen(false);
      setManualCandidates(null);
      setManualQuery('');
      setManualLoading(false);
      setSelectedId(null);   // selectedId는 ambiguous picker와 공유 — 전이 후 새 컨텍스트로 선택 새는 것 차단(R3)
      manualReqRef.current++;
    }
  }, [uiStatus]);

  useEffect(() => {
    if (previousQueueVersionRef.current === queueVersion) return;
    previousQueueVersionRef.current = queueVersion;
    manualReqRef.current++;
    setManualOpen(false);
    setManualCandidates(null);
    setManualQuery('');
    setManualLoading(false);
    setSelectedId(null);
    setFastConfirming(false);

    if (loading) {
      wasInvalidatedWhileLoadingRef.current = true;
    } else {
      setGuard(null);
    }
  }, [loading, queueVersion]);

  // 언마운트 시 in-flight 로드 무효화 — reqId 가드가 언마운트 후 stale toast.error/setState를 막는다(R2 M6).
  useEffect(() => () => { manualReqRef.current++; }, []);

  // picker 열리면 검색 input으로 focus 이동(트리거 버튼이 사라져 키보드 focus 상실 방지, R2 M8).
  useEffect(() => { if (manualOpen) manualInputRef.current?.focus(); }, [manualOpen]);

  const openManualLink = async (preselectId?: number) => {
    const reqId = ++manualReqRef.current;
    setManualOpen(true);
    setSelectedId(null);
    setManualQuery('');
    setManualLoading(true);
    try {
      const rows = await onLoadManualCandidates(deposit);
      if (reqId !== manualReqRef.current) return;
      setManualCandidates(rows);
      if (preselectId != null && rows.some((r) => r.id === preselectId)) {
        setSelectedId(preselectId);   // amber: 추천 주문 미리선택(검색으로 가려지지 않은 경우만)
      }
    } catch {
      if (reqId !== manualReqRef.current) return;
      toast.error('주문 목록을 불러오지 못했습니다');
      setManualOpen(false);
    } finally {
      if (reqId === manualReqRef.current) setManualLoading(false);
    }
  };

  const closeManualLink = () => {
    manualReqRef.current++;  // 진행 중 로드 무효화
    setManualOpen(false);
    setSelectedId(null);
    setManualQuery('');
  };

  // amount_mismatch 단건 fast-path: 바로 확정 전에 freshness+uniqueness 재검증(구 picker fetch 게이트 복원,
  // codexloop R2/R3). fresh fetch 후 (a) 추천 주문이 아직 미지정이고 (b) 그 닉네임의 유일한 미지정 주문일 때만
  // fresh row로 즉시 확정. 배정/완료됐거나 렌더 후 동명 주문이 새로 생겼으면 picker로 폴백(경쟁 주문 노출).
  // happy-path에선 picker를 열지 않는다(폴백에서만).
  const confirmSingleFresh = async () => {
    if (!suggested) return;
    const order = suggested;
    const reqId = ++manualReqRef.current;
    setFastConfirming(true);
    try {
      const rows = await onLoadManualCandidates(deposit);
      if (reqId !== manualReqRef.current) return;
      const freshRow = rows.find((r) => r.id === order.id);
      const sameName = rows.filter((r) => r.nickname === order.nickname);
      if (freshRow && sameName.length === 1) {
        openConfirmFor(freshRow);
      } else {
        setManualOpen(true);
        setManualCandidates(rows);
        setManualQuery('');
        setSelectedId(null);
        toast.info('추천 주문이 바뀌었어요. 주문을 확인하고 선택해주세요');
      }
    } catch {
      if (reqId === manualReqRef.current) toast.error('주문 목록을 불러오지 못했습니다');
    } finally {
      setFastConfirming(false);  // 로딩 플래그(데이터 write 아님)라 reqId 무관하게 해제
    }
  };

  // 참고 후보 [매칭](스펙 §6.2): fresh 재검증 통과 시에만 breakdown 다이얼로그.
  // 조건 깨짐(배정/완료/금액 편집) → 다이얼로그 열지 않고 안내 + 목록 자연 갱신 유도.
  const confirmReferenceFresh = async (order: OrderCandidate) => {
    const reqId = ++manualReqRef.current;
    setFastConfirming(true);
    try {
      const rows = await onLoadManualCandidates(deposit);   // Task 2에서 -60 merge됨
      if (reqId !== manualReqRef.current) return;
      const freshRow = rows.find((r) => r.id === order.id);
      if (freshRow && freshRow.expectedAmounts.includes(deposit.amount)) {
        openConfirmFor(freshRow);
      } else {
        toast.info('이 주문의 상태가 바뀌었어요. 최신 목록으로 확인해주세요');
        setManualOpen(true);
        setManualCandidates(rows);
        setManualQuery('');
        setSelectedId(null);
        // 스펙 §6.2: 재검증 실패 = 서버 상태가 이미 바뀐 것 — 큐도 재조회해 stale 참고 행이
        // 최신 스냅샷으로 재도출되게 한다(fire-and-forget, picker는 그대로 열어둔 채 refresh 결과가 반영됨).
        onRequestRefresh?.();
      }
    } catch {
      if (reqId === manualReqRef.current) toast.error('주문 목록을 불러오지 못했습니다');
    } finally {
      setFastConfirming(false);
    }
  };

  // ambiguous 표시 순서만 comparator(스펙 §3, R3 F3) — derive 출력·집합·상한·픽커 진입 불변.
  const displayCandidates = useMemo(() => {
    if (uiStatus !== 'ambiguous') return candidates;
    const depositDateKst = toKstDateString(deposit.deposited_at ?? deposit.created_at);
    return [...candidates].sort(compareReferenceCandidates(depositDateKst, deposit.amount));
  }, [uiStatus, candidates, deposit]);

  // amber 후보(=candidates, service의 byName)는 입금자명 일치·금액 불일치. picker 배지/정렬용.
  const nameMatchedIds = useMemo(
    () => (uiStatus === 'amount_mismatch' ? new Set(candidates.map((c) => c.id)) : undefined),
    [uiStatus, candidates],
  );

  // 검색 필터 + 금액맞는 건(유력) 위로 정렬을 1회만(렌더마다 재계산 방지). filter가 새 배열이라 원본 비파괴.
  const visibleManualCandidates = useMemo(() => {
    const q = manualQuery.trim().toLowerCase();
    return (manualCandidates ?? [])
      .filter((c) => c.nickname.toLowerCase().includes(q))
      .sort((a, b) => {
        const an = nameMatchedIds?.has(a.id) ? 0 : 1;
        const bn = nameMatchedIds?.has(b.id) ? 0 : 1;
        if (an !== bn) return an - bn;
        const am = a.expectedAmounts.includes(deposit.amount) ? 0 : 1;
        const bm = b.expectedAmounts.includes(deposit.amount) ? 0 : 1;
        return am - bm;
      });
  }, [manualCandidates, manualQuery, deposit.amount, nameMatchedIds]);

  // 선택이 현재 보이는 목록에 있을 때만 연결 가능(검색으로 가려진 주문에 매칭되는 것 차단).
  const manualSelectionVisible = selectedId !== null && visibleManualCandidates.some((c) => c.id === selectedId);

  // 구제 행은 요약/상세 표시도 구제 대상 주문으로 정렬 — candidates[0](최근접 델타)와 구제 대상이
  // 다른 주문일 때 행은 A를 보여주고 버튼은 B를 확정하는 표시 불일치 방지(codexloop 구현 R1 #2).
  const suggested = item.shippingRescue
    ? candidates.find((c) => c.id === item.shippingRescue!.orderId) ?? candidates[0] ?? null
    : candidates[0] ?? null;

  // 입금액에 가장 가까운 acceptableAmount(배송비 포함 변형 등). 부족/초과 판정의 기준액 —
  // 델타 라벨과 확정 모달 경고가 '같은 nearest'를 써야 부호·문구가 일치(codexloop R3 합의).
  const nearestExpected = (order: OrderCandidate): number =>
    order.expectedAmounts.reduce(
      (p, a) => (Math.abs(a - deposit.amount) < Math.abs(p - deposit.amount) ? a : p),
      order.expectedAmounts[0] ?? order.total_price,
    );

  // amber 수축행 델타: 기준액(nearest) 대비 부족/초과. total_price를 쓰면 배송비 변형에서 부호가
  // 뒤집히므로 nearest 유지, 라벨은 '주문'이 아닌 '예상'(nearest≠주문총액일 수 있음).
  const amberReconcile = (order: OrderCandidate) => {
    const nearest = nearestExpected(order);
    const diff = deposit.amount - nearest;
    return { orderAmount: nearest, absLabel: won(Math.abs(diff)), short: diff >= 0 ? '초과' : '부족' };
  };

  const openConfirmFor = (order: OrderCandidate) => {
    const projected = order.confirmedSum + deposit.amount;
    const willComplete = projected >= order.paidThreshold;
    // 타이틀은 '무슨 일이 일어나는지' 한 줄만 — 금액은 칩(이번 입금)과 breakdown 표가 운반
    // (오너 지적: 타이틀에 사실 3개 압축 + '—' 금지). breakdown 기준액 = paidThreshold(잔액 계산과
    // 동일 baseline — nearest 혼용 금지).
    let title: string;
    let description: string | undefined;
    let warning: string | undefined;
    let notice: string | undefined;
    let rows: Array<{ label: string; value: string; strong?: boolean }> | undefined;
    const baseRows = [
      { label: '정산서 금액', value: won(order.paidThreshold) },
      ...(order.confirmedSum > 0 ? [{ label: '기존 입금', value: won(order.confirmedSum) }] : []),
    ];
    if (!willComplete) {
      // 부분 입금으로 확정 (1차든 N차든 합계가 임계값 미달). 정식 플로우라 amber 챌린지 대신 blue 안내.
      // ★용어 통일(오너 2026-07-06): 유저 노출 = '부분 입금'(상태명·띄어쓰기)·'잔금'만.
      //   '분할입금'은 내부 기능명 전용, '잔액' 금지.
      title = `${order.nickname}님 주문을 부분 입금으로 확정`;
      description = "확정하면 주문이 '부분 입금' 상태가 됩니다.";
      rows = [...baseRows, { label: '남은 잔금', value: won(order.paidThreshold - projected), strong: true }];
      notice = '잔금은 다음 입금이 들어오면 이어서 확정할 수 있어요';
    } else if (order.status === '부분 입금') {
      // 잔금 완납. 초과 입금만 챌린지(부분 입금 주문의 expectedAmounts=[잔금] → nearest=잔금이므로
      // '정산서 금액'이 아니라 '남은 잔금'으로 라벨).
      title = `${order.nickname}님 주문 잔금 확정`;
      description = "확정하면 주문이 '입금 완료' 상태가 됩니다.";
      rows = [...baseRows, { label: '남은 잔금', value: '0원 (완납)', strong: true }];
      if (!order.expectedAmounts.includes(deposit.amount)) {
        warning = `입금 ${won(deposit.amount)}이 남은 잔금 ${won(nearestExpected(order))}보다 많아요.\n그래도 확정하시겠어요?`;
      }
    } else {
      // 직행 완납. 경고 기준액은 nearest(정산서 금액) — 행 델타와 동일 baseline이라 부호/숫자가 어긋나지 않음.
      title = `${order.nickname}님 주문을 '입금 완료'로 변경`;
      if (!order.expectedAmounts.includes(deposit.amount)) {
        warning = `입금 ${won(deposit.amount)}이 정산서 금액 ${won(nearestExpected(order))}과 달라요.\n그래도 확정하시겠어요?`;
      }
    }
    setGuard({ mode: 'confirm', order, title, description, warning, notice, rows });
  };

  // 배송비 구제 확정(스펙 §5, confirm-first). 카드 클릭 시 재검증(fresh 재판정) → 통과 시에만 ConfirmGuard.
  // 탈락(주문 변경·프리셋 삭제·유일성 상실·override 생김·이미 확정) → 제안 철회 + refetch(§5.1).
  const handleRescueClick = async () => {
    const rescue = item.shippingRescue;
    if (!rescue || !onRescueRevalidate || !onRescueConfirm) return;
    const reqId = ++manualReqRef.current;
    setFastConfirming(true);
    try {
      const fresh = await onRescueRevalidate(match.id, rescue.orderId, rescue.presetAmount);
      if (reqId !== manualReqRef.current) return;
      if (!fresh) {
        toast.info('제안 조건이 바뀌었어요. 최신 목록에서 다시 확인해주세요');
        onRequestRefresh?.();
        return;
      }
      // 완납 금액·상품 금액·적용 배송비를 breakdown으로(스펙 §4·§5.2 ConfirmGuard rows/notice 패턴).
      setGuard({
        mode: 'rescue',
        orderId: fresh.order.id,
        presetAmount: fresh.presetAmount,
        title: `${fresh.order.nickname}님 주문을 '입금 완료'로 처리`,
        description: "확정하면 주문이 '입금 완료' 상태가 됩니다.",
        notice: `확정하면 이 주문의 배송비가 ${won(fresh.presetAmount)}으로 기록됩니다`,
        rows: [
          { label: '상품 금액', value: won(fresh.order.total_price) },
          { label: '다른 배송비', value: won(fresh.presetAmount) },
          { label: '완납 금액', value: won(fresh.expectedTotal), strong: true },
        ],
      });
    } catch {
      if (reqId === manualReqRef.current) toast.error('주문 정보를 불러오지 못했습니다');
    } finally {
      setFastConfirming(false);
    }
  };

  // 주문 상세 인라인 패널: 품목 요약 + 상품비/배송비 세분. 배송비 = 정산서금액(nearest) − 상품비(total_price)
  // 로 뽑아 델타 라인·모달 경고와 항상 일치(배송비 변형서도 합계가 안 어긋남). 품목은 요약 문자열만(요약 한 줄).
  const renderOrderDetail = (order: OrderCandidate) => {
    const settlement = nearestExpected(order);
    const shipping = settlement - order.total_price;
    return (
      <div id={`deposit-detail-${item.match.id}`} className="border-t border-slate-100 bg-slate-50/70 px-5 py-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg bg-white p-2.5 text-[13px] text-slate-600 ring-1 ring-slate-200">
            🛍 <span className="font-medium text-slate-800">{order.items_summary || '품목 정보 없음'}</span>
          </div>
          <div className="rounded-lg bg-white p-2.5 text-[13px] tabular-nums ring-1 ring-slate-200">
            <div className="flex justify-between"><span className="text-slate-500">상품 비용</span><span>{won(order.total_price)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">배송비</span><span>{shipping > 0 ? won(shipping) : '무료'}</span></div>
            <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 font-semibold"><span>정산서 금액</span><span>{won(settlement)}</span></div>
          </div>
        </div>
      </div>
    );
  };

  // 후보 근거 텍스트(스펙 §5): 무엇과 일치했는지를 기존 다이얼로그 어휘로 명시(오너 카피 피드백
  // 2026-07-06 — '완납/상품비 일치'는 불명확). 정산서 금액 = 상품비+배송비(paidThreshold),
  // 부분 입금 주문은 남은 잔금, 상품비만 일치는 배송비 제외를 괄호로 명시. D11 준수.
  const refDateLabel = (orderDate: string): string => {
    const today = new Date(); const kst = (d: Date) => format(d, 'yyyy-MM-dd');
    if (orderDate === kst(today)) return '오늘 주문';
    const y = new Date(today.getTime() - 86400000);
    if (orderDate === kst(y)) return '어제 주문';
    return `${orderDate.slice(5).replace('-', '/')} 주문`;
  };
  const refMatchLabel = (order: OrderCandidate): string => {
    if (!isFullPaymentMatch(order, deposit.amount)) return '상품 금액만 일치 (배송비 제외)';
    return order.status === '부분 입금' ? '남은 잔금 일치' : '정산서 금액 일치';
  };
  const refReason = (order: OrderCandidate): string =>
    `${won(deposit.amount)} ${refMatchLabel(order)} · ${refDateLabel(order.order_date)}`;

  const renderReferenceList = (refs: OrderCandidate[]) => (
    <div className="mt-2 border-t border-dashed border-slate-200">
      {refs.map((order) => (
        <div key={order.id} className="flex items-center gap-2.5 border-b border-dashed border-slate-200 px-1 py-2 text-[13px]">
          <span className="min-w-[110px] truncate font-semibold text-slate-700">{order.nickname}</span>
          <span className="flex-1 truncate text-xs text-slate-400">{refReason(order)}</span>
          <button
            type="button"
            aria-label={`${won(deposit.amount)} ${deposit.depositor_name} 입금을 ${order.nickname} 주문에 매칭`}
            className="h-7 rounded-md border border-slate-300 bg-white px-3 text-xs font-medium text-slate-600 hover:border-blue-500 hover:text-blue-600 disabled:opacity-50"
            disabled={fastConfirming || loading}
            onClick={() => void confirmReferenceFresh(order)}
          >
            매칭
          </button>
        </div>
      ))}
    </div>
  );

  const handleConfirmClick = () => {
    if (uiStatus === 'ambiguous') {
      const chosen = candidates.find((c) => c.id === selectedId);
      if (chosen) openConfirmFor(chosen);
    } else if (suggested) {
      openConfirmFor(suggested);
    }
  };

  const handleUndoClick = () => {
    const name = confirmedOrder?.nickname ?? '해당';
    setGuard({ mode: 'undo', title: `${name}님 주문 확정을 취소합니다` });
  };

  const handleRejectClick = () => {
    // 제외는 되돌릴 수 있는 가벼운 동작 — 노란 ⚠️ 경고 박스(warning) 미사용. 차분한 description 한 줄로 안내.
    setGuard({
      mode: 'reject',
      title: '주문 매칭에서 제외합니다',
    });
  };

  const handleRestoreClick = () => {
    setGuard({ mode: 'restore', title: `이 입금을 다시 확인 대기로 되돌립니다` });
  };

  const handleGuardConfirm = async () => {
    if (!guard) return;
    wasInvalidatedWhileLoadingRef.current = false;
    setLoading(true);
    try {
      if (guard.mode === 'rescue') {
        // 확정 체인은 훅이 소유(제출 재검증 → Step 1 확정 → Step 2 CAS). Step 1 하드 실패는 throw →
        // 아래 catch(기존 stale/settled/일시오류 UX). 여기 도달 = Step 1 성공(또는 제출 재검증 철회).
        const outcome = await onRescueConfirm!(match.id, guard.orderId, guard.presetAmount);
        if (outcome === 'withdrawn') {
          toast.info('제안 조건이 바뀌었어요. 최신 목록에서 다시 확인해주세요');
        } else if (outcome === 'shipping-unrecorded') {
          // 돈은 확정됐으나 Step 2 미반영 — 고객-표시 불일치라 '무해' 취급 않고 행동 경로 안내(스펙 §5.4).
          toast.error(`입금은 확정됐지만 주문 배송비 기록(${won(guard.presetAmount)})이 반영되지 않았습니다. 정산서 화면에서 배송비를 확인해주세요.`);
        } else {
          toast.success('입금이 확정되었습니다');
        }
      } else if (guard.mode === 'confirm') {
        // match.modified_at = 행이 로드된 시점의 버전. 그 사이 제외/복구/변경됐으면 stale로 거부됨(주문 오변경 차단).
        await onConfirm(match.id, guard.order, match.modified_at);
        toast.success('입금이 확정되었습니다');
      } else if (guard.mode === 'undo') {
        // 구제 undo 원복(스펙 §5): pre-undo 소스가 'deposits_rescue'이고 override가 남아있을 때만 원복 요청.
        // undo 후엔 confirmed_source가 NULL로 지워지므로 여기(호출 전)서 판정해 넘긴다.
        // p는 현재 override가 아니라 구제 프리셋을 재유도(amount − total): 셀러가 그 사이 수동 변경한
        // override(≠프리셋)는 CAS 미발화로 보존되고, total이 변했으면 미발화=fail-safe(T3 리뷰 Critical 수용).
        const derivedPreset = confirmedOrder ? deposit.amount - confirmedOrder.total_price : null;
        const rescueRevert =
          match.confirmed_source === 'deposits_rescue' && confirmedOrder &&
          derivedPreset != null && Number.isInteger(derivedPreset) && derivedPreset > 0
            ? { orderId: confirmedOrder.id, presetAmount: derivedPreset }
            : undefined;
        await onUndo(
          match.id,
          confirmedOrder?.paidThreshold ?? null,
          confirmedOrder?.total_price ?? null,
          confirmedOrder?.shippingFeeOverride ?? null,
          // ⚠deprecated(2026-07-27, 마이그 20260727220000): undo RPC는 이 지문을 보지 않는다(§3.3 반전).
          // 아래 rescue null 고정(§3.6)도 이제 무동작 — 방어선으로 오인하지 말 것. 전달만 유지한다.
          // 구 계약: 확정 행 페이로드에 로드 시 부착된 지문만 전달(임계값과 같은 config 스냅샷 보장).
          match.confirmed_source === 'deposits_rescue' ? null : (confirmedOrder?.settingsFingerprint ?? null),
          rescueRevert,
        );
        toast.success('확정을 취소했습니다');
      } else if (guard.mode === 'reject') {
        await onReject?.(match.id);
        toast.success('주문 매칭에서 제외했습니다');
      } else {
        await onRestore?.(match.id);
        toast.success('확인 대기로 옮겼습니다');
      }
      wasInvalidatedWhileLoadingRef.current = false;
      setGuard(null);
    } catch (err) {
      // stale(다른 탭이 그 사이 변경): 재시도해도 같은 버전이라 또 실패 → 가드를 닫고 새로고침된 상태를 보게 한다.
      // (useDeposits.confirm이 stale 시 큐를 이미 refresh함 — codexloop 제외 R3 F2.)
      if (isStaleDepositError(err)) {
        wasInvalidatedWhileLoadingRef.current = false;
        toast.info('입금 상태가 바뀌었습니다. 최신 목록에서 다시 확인해주세요');
        setGuard(null);
      } else if (isSettledOrderDepositError(err)) {
        // P0002 = 그 사이 주문이 완납/출고됨 — 재시도 무의미, 가드 닫고 새로고침된 목록으로.
        wasInvalidatedWhileLoadingRef.current = false;
        toast.info('이미 입금·출고 처리가 끝난 주문이에요. 최신 목록에서 확인해주세요');
        setGuard(null);
      } else if (wasInvalidatedWhileLoadingRef.current) {
        wasInvalidatedWhileLoadingRef.current = false;
        toast.info('처리 중 목록이 변경됐습니다. 최신 상태에서 다시 확인해주세요');
        setGuard(null);
      } else {
        const failMsg: Record<NonNullable<GuardState>['mode'], string> = {
          confirm: '확정에 실패했습니다. 다시 시도해주세요',
          rescue: '확정에 실패했습니다. 다시 시도해주세요',
          undo: '취소에 실패했습니다. 다시 시도해주세요',
          reject: '제외에 실패했습니다. 다시 시도해주세요',
          restore: '복구에 실패했습니다. 다시 시도해주세요',
        };
        toast.error(failMsg[guard.mode]);
        // Keep guard open so the user can retry (일시 오류).
      }
    } finally {
      setLoading(false);
    }
  };

  // 확정행의 matched_order_id가 NULL = 매칭된 주문(정산서)이 삭제된 상태(FK ON DELETE SET NULL).
  // FK 진실값(matched_order_id)으로 판정한다 — !confirmedOrder로 추론하면 id는 살아있는데 조회가
  // 빈 결과인 무결성/RLS 이상 케이스까지 '삭제됨'으로 오인. 삭제면 되돌릴 주문이 없어 undo 비활성
  // (오너 결정). '출고 완료'와 사유가 달라 툴팁 분기.
  const orderDeleted = uiStatus === 'confirmed' && match.matched_order_id === null;
  const undoDisabled = orderDeleted || confirmedOrder?.status === '출고 완료';
  const undoDisabledReason = orderDeleted
    ? '삭제된 주문의 입금은 되돌릴 수 없습니다'
    : '이미 출고된 주문은 되돌릴 수 없습니다';

  const depositDateTime = formatDepositDateTime(deposit.deposited_at ?? deposit.created_at);

  const isConfident = uiStatus === 'matched' || uiStatus === 'amount_suggested';
  const amountMatches = suggested?.expectedAmounts.includes(deposit.amount) ?? false;

  return (
    <>
      <div
        id={`deposit-row-${item.match.id}`}
        className={cn('relative overflow-hidden rounded-2xl bg-white shadow-sm ring-1', meta.ring, highlighted && 'ring-2 ring-blue-400 ring-offset-2')}
      >
        <span className={cn('absolute inset-y-0 left-0 w-[3px]', meta.rail)} aria-hidden />

        {/* 메인 줄: 금액 hero · (상태별 본문) · 액션 */}
        <div className="flex flex-wrap items-center gap-4 py-3.5 pl-5 pr-3.5">
          {/* 입금액·입금일시·입금자를 라벨 그리드로 명시(일관성 위해 3필드 전부 라벨).
              은행은 단일 연동계좌라 항상 같은 값 → 표시 생략(저장/bank_name invariant는 유지).
              고정폭 — 입금자명 길이에 따라 칸이 늘어나면 옆 본문(추천 매칭 없음·추천 주문·화살표)
              시작점이 행마다 어긋나므로 폭을 고정한다. 라벨열은 3.5rem 고정, 값열이 truncate. */}
          <div className="w-[220px] shrink-0">
            <div className="grid grid-cols-[3.5rem_1fr] items-baseline gap-x-2 gap-y-1">
              <span className="text-[11px] text-slate-400">입금액</span>
              <span className="text-[18px] font-bold tabular-nums text-slate-900">
                {won(deposit.amount)}
              </span>
              {depositDateTime && (
                <>
                  <span className="text-[11px] text-slate-400">입금일시</span>
                  <span className="text-[12px] tabular-nums text-slate-600">{depositDateTime}</span>
                </>
              )}
              {deposit.depositor_name && (
                <>
                  <span className="text-[11px] text-slate-400">입금자</span>
                  <span className="min-w-0 truncate text-[13px] font-medium text-slate-800">
                    {deposit.depositor_name}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* 확실: matched(이름+금액)=초록 원탭 '입금완료 처리' + ✓초록 체크 / amount_suggested(금액만
              일치·이름↔주문 연결 미학습)=중립 검정 '확인' + 체크 없는 '금액 일치'. 확신도 차이는 버튼·체크로
              신호하고 텍스트는 '금액 일치'만 명시(검증된 사실만; '미확인' 단정 회피 — codexloop R2/R3 F4). */}
          {isConfident && suggested && (() => {
            const isNameConfident = uiStatus === 'matched';
            return (
            <>
              <ArrowRight className="h-4 w-4 shrink-0 text-slate-300" aria-hidden />
              <div className="min-w-0 flex-1">
                {/* 1행 = 닉네임(유일한 shrink 대상) + 날짜 + 상세. pill/버튼은 nowrap+shrink-0 —
                    폭이 좁아지면 닉네임만 truncate되고 pill 내부 줄바꿈/버튼 세로깨짐이 없어야 한다. */}
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate font-semibold text-slate-900">{suggested.nickname}</span>
                  <span className="shrink-0 whitespace-nowrap rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] tabular-nums text-slate-500">
                    {suggested.order_date}
                  </span>
                  <button
                    type="button"
                    aria-expanded={detailOpen}
                    aria-controls={`deposit-detail-${item.match.id}`}
                    aria-label={detailOpen ? '주문 상세 접기' : '주문 상세 보기'}
                    className="ml-0.5 inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap rounded text-[11px] font-medium text-slate-400 hover:text-slate-600"
                    onClick={() => setDetailOpen((v) => !v)}
                  >
                    상세 <span className="text-[9px]" aria-hidden>{detailOpen ? '▲' : '▼'}</span>
                  </button>
                </div>
                {/* 2행 = 금액 사실들(일치 여부 + 잔액). 잔액 pill은 1행에서 내려 닉네임 공간 확보. */}
                <div className="mt-0.5 flex items-center gap-1.5 text-[11px]">
                  {amountMatches ? (
                    isNameConfident ? (
                      <span className="inline-flex shrink-0 items-center gap-1">
                        <Check className="h-3 w-3 text-emerald-600" aria-hidden />
                        <span className="whitespace-nowrap text-slate-500">금액 일치</span>
                      </span>
                    ) : (
                      <span className="shrink-0 whitespace-nowrap text-slate-400">금액 일치</span>
                    )
                  ) : (
                    <span className="truncate text-slate-400">{suggested.items_summary || '금액 확인 필요'}</span>
                  )}
                  {suggested.remainingAmount !== null && suggested.remainingAmount > 0 && (
                    <span className="shrink-0 whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
                      잔금 {won(suggested.remainingAmount)}
                    </span>
                  )}
                </div>
              </div>
              {!manualOpen && (
                <>
                  <button
                    type="button"
                    aria-haspopup="dialog"
                    aria-label={
                      isNameConfident
                        ? `${won(deposit.amount)} ${deposit.depositor_name} 입금을 ${suggested.nickname} 주문 입금완료 처리`
                        : `${won(deposit.amount)} ${deposit.depositor_name} 입금을 ${suggested.nickname} 주문으로 확인`
                    }
                    className={cn(
                      ACTION_BASE,
                      'shrink-0 px-4 text-white',
                      isNameConfident
                        ? 'bg-emerald-700 shadow-sm hover:bg-emerald-800'
                        : 'bg-slate-900 hover:bg-slate-800',
                    )}
                    onClick={handleConfirmClick}
                  >
                    {isNameConfident ? '입금완료 처리' : '확인'}
                  </button>
                  {/* 추천이 틀린 경우(금액 우연 일치 등) 탈출구 — 미지정 주문 picker로 다른 주문 선택. */}
                  <button
                    type="button"
                    aria-label={`${won(deposit.amount)} ${deposit.depositor_name} 입금을 다른 주문에 매칭`}
                    className={cn(ACTION_BASE, 'shrink-0 px-3 font-medium text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50')}
                    onClick={() => openManualLink()}
                  >
                    다른 주문
                  </button>
                </>
              )}
            </>
            );
          })()}

          {/* amber(이름 맞음·금액 틀림): 추천 주문 + 부족/초과 식 + 두 갈림길
              [이대로 입금완료 처리](바로 확정 모달) / [다른 주문](picker 펼침). 경고를 선택지로 전환. */}
          {uiStatus === 'amount_mismatch' && suggested && (
            <>
              <ArrowRight className="h-4 w-4 shrink-0 text-slate-300" aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate font-semibold text-slate-900">{suggested.nickname}</span>
                  <span className="shrink-0 whitespace-nowrap rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] tabular-nums text-slate-500">
                    {suggested.order_date}
                  </span>
                  <button
                    type="button"
                    aria-expanded={detailOpen}
                    aria-controls={`deposit-detail-${item.match.id}`}
                    aria-label={detailOpen ? '주문 상세 접기' : '주문 상세 보기'}
                    className="ml-0.5 inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap rounded text-[11px] font-medium text-slate-400 hover:text-slate-600"
                    onClick={() => setDetailOpen((v) => !v)}
                  >
                    상세 <span className="text-[9px]" aria-hidden>{detailOpen ? '▲' : '▼'}</span>
                  </button>
                </div>
                {/* 구제 제안이 있으면 '초과 N원' 델타 대신 아래 파란 제안 블록으로 대체(스펙 §4 — 시각 우선순위 대체). */}
                {!item.shippingRescue && (() => {
                  // 핵심(부족/초과액)을 앞에 두어 잘려도 살아남게. 입금액은 왼쪽에 이미 있어 식에서 생략.
                  // 기준은 '정산서 금액'(nearest) — 확정 모달 경고와 동일 baseline(주문총액≠nearest일 수 있음).
                  const recon = amberReconcile(suggested);
                  return (
                    <div className="mt-0.5 truncate text-[11px] tabular-nums text-amber-700">
                      <b className="font-semibold">{recon.absLabel} {recon.short}</b>
                      <span className="text-slate-500"> · 정산서 금액 {won(recon.orderAmount)}</span>
                    </div>
                  );
                })()}
              </div>
              {!item.shippingRescue && !manualOpen && (
                candidates.length > 1 ? (
                  // 같은 이름 주문이 여러 건 → 휴리스틱 1건 바로 확정은 엉뚱한 건 확정 위험. picker로 경쟁 주문 노출.
                  <button
                    type="button"
                    aria-haspopup="dialog"
                    aria-label={`${won(deposit.amount)} ${deposit.depositor_name} 입금 — 같은 이름 주문 ${candidates.length}건 중 선택`}
                    className={cn(ACTION_BASE, 'shrink-0 bg-slate-900 px-4 text-white hover:bg-slate-800')}
                    onClick={() => openManualLink(suggested.id)}
                  >
                    주문 선택
                  </button>
                ) : (
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      aria-haspopup="dialog"
                      disabled={fastConfirming}
                      aria-label={`${won(deposit.amount)} ${deposit.depositor_name} 입금을 ${suggested.nickname} 주문 입금완료 처리(금액 ${amberReconcile(suggested).short} 확인)`}
                      className={cn(ACTION_BASE, 'bg-slate-900 px-4 text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60')}
                      onClick={confirmSingleFresh}
                    >
                      이대로 입금완료 처리
                    </button>
                    <button
                      type="button"
                      aria-label={`${won(deposit.amount)} ${deposit.depositor_name} 입금을 다른 주문에 매칭`}
                      className={cn(ACTION_BASE, 'px-3 font-medium text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50')}
                      onClick={() => openManualLink()}
                    >
                      다른 주문
                    </button>
                  </div>
                )
              )}
            </>
          )}

          {/* 배송비 구제 제안(스펙 §4·§5, PR2): amber 행에 shippingRescue가 붙으면 '초과 N원' 델타 대신
              파란 제안 블록 + 원클릭 확정. blue=제안 언어(amber=대기, blue=제안, 기존 카드 색 문법 준수).
              picker 열리면 숨김(수동연결 흐름과 배타 — !manualOpen). 계산값은 §3 산출(shippingRescue)만 사용. */}
          {uiStatus === 'amount_mismatch' && item.shippingRescue && suggested && !manualOpen && (() => {
            const rescue = item.shippingRescue;
            const targetOrder = candidates.find((c) => c.id === rescue.orderId) ?? suggested;
            return (
              <div className="mt-2 w-full">
                <div className="rounded-xl bg-blue-50/70 p-3.5 ring-1 ring-blue-200">
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
                    <p className="min-w-0 flex-1 break-keep text-[13px] leading-relaxed text-slate-700">
                      <span className="font-semibold text-blue-700">제안</span>
                      {' · '}
                      <span className="font-medium text-slate-900">{targetOrder.nickname}</span>님 주문{' '}
                      <span className="font-medium tabular-nums text-slate-900">{won(targetOrder.total_price)}</span>
                      {' + 다른 배송비 '}
                      <span className="font-medium tabular-nums text-slate-900">{won(rescue.presetAmount)}</span>
                      <span className="whitespace-nowrap text-blue-700">
                        {' = '}
                        <span className="font-semibold tabular-nums">{won(rescue.expectedTotal)}</span> 정확히 일치
                      </span>
                    </p>
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        aria-haspopup="dialog"
                        disabled={fastConfirming || loading}
                        aria-label={`${won(deposit.amount)} ${deposit.depositor_name} 입금을 ${targetOrder.nickname} 주문 배송비 ${won(rescue.presetAmount)}으로 맞춰 입금 완료 처리`}
                        className={cn(ACTION_BASE, 'bg-blue-600 px-4 text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60')}
                        onClick={() => void handleRescueClick()}
                      >
                        배송비 {won(rescue.presetAmount)}으로 입금 완료 처리
                      </button>
                      <button
                        type="button"
                        aria-label={`${won(deposit.amount)} ${deposit.depositor_name} 입금을 다른 주문에 매칭`}
                        className={cn(ACTION_BASE, 'px-3 font-medium text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50')}
                        onClick={() => openManualLink()}
                      >
                        다른 주문
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* amber 병기: 참고 후보(스펙 §3 — amber의 기존 표시는 그대로 두고 아래에 병기) */}
          {uiStatus === 'amount_mismatch' && (item.referenceCandidates?.length ?? 0) > 0 && !manualOpen && (
            <div className="mt-2 w-full">
              <span className="rounded bg-slate-100 px-2 py-0.5 text-[11.5px] font-semibold text-slate-500">
                후보 {item.referenceCandidates!.length}
              </span>
              {renderReferenceList(item.referenceCandidates!)}
            </div>
          )}

          {/* 미매칭: 참고 후보 있으면 배지+리스트+[다른 주문에 매칭], 없으면 기존 "추천 매칭 없음"+[주문에 매칭].
              picker 열리면 아래 섹션이 받음. */}
          {uiStatus === 'unmatched' && (
            <>
              {(item.referenceCandidates?.length ?? 0) > 0 && !manualOpen ? (
                <div className="w-full">
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-[11.5px] font-semibold text-slate-500">
                    후보 {item.referenceCandidates!.length}
                  </span>
                  {renderReferenceList(item.referenceCandidates!)}
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      aria-label={`${won(deposit.amount)} ${deposit.depositor_name} 입금을 다른 주문에 매칭`}
                      className={cn(ACTION_BASE, 'shrink-0 bg-amber-500 px-4 text-white hover:bg-amber-600')}
                      onClick={() => openManualLink()}
                    >
                      다른 주문에 매칭
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex-1 text-[13px] text-slate-400">추천 매칭 없음</div>
                  {!manualOpen && (
                    <button
                      type="button"
                      aria-label={`${won(deposit.amount)} ${deposit.depositor_name} 입금을 주문에 수동 매칭`}
                      className={cn(ACTION_BASE, 'shrink-0 bg-amber-500 px-4 text-white hover:bg-amber-600')}
                      onClick={() => openManualLink()}
                    >
                      주문에 매칭
                    </button>
                  )}
                </>
              )}
            </>
          )}

          {/* 처리 완료: 매칭된 주문 + 되돌림 */}
          {uiStatus === 'confirmed' && (
            <>
              <ArrowRight className="h-4 w-4 shrink-0 text-slate-300" aria-hidden />
              <div className="min-w-0 flex-1">
                {confirmedOrder ? (
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate font-semibold text-slate-900">{confirmedOrder.nickname}</span>
                    <span className="shrink-0 whitespace-nowrap rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] tabular-nums text-slate-500">
                      {confirmedOrder.order_date}
                    </span>
                  </div>
                ) : match.matched_order_id === null ? (
                  // 주문(정산서)이 삭제됨(FK SET NULL) — 의도된 상태라 경보색 아님
                  <span className="text-[13px] font-medium text-slate-500">매칭된 주문이 삭제됨</span>
                ) : (
                  // 이상 케이스: id는 있으나 주문 조회가 빈 결과(무결성/RLS) — 기존 경보 문구 유지
                  <span className="text-[13px] font-medium text-amber-600">확인 필요 — 매칭된 주문을 찾을 수 없습니다</span>
                )}
              </div>
              {undoDisabled ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex shrink-0">
                        <button
                          type="button"
                          className={cn(ACTION_BASE, 'px-3 font-medium text-slate-300')}
                          disabled
                        >
                          되돌림
                        </button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{undoDisabledReason}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <button
                  type="button"
                  aria-haspopup="dialog"
                  aria-label={`${confirmedOrder?.nickname ?? '해당'}님 주문 확정 되돌리기`}
                  className={cn(ACTION_BASE, 'shrink-0 px-3 font-medium text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50')}
                  onClick={handleUndoClick}
                >
                  되돌림
                </button>
              )}
            </>
          )}

          {/* 매칭 제외: 주문 매칭에서 제외된 입금 + [복구]. 섹션 헤더가 이미 '매칭 제외'라 행 라벨은 생략. */}
          {uiStatus === 'excluded' && onRestore && (
            <button
              type="button"
              aria-haspopup="dialog"
              aria-label={`${won(deposit.amount)} ${deposit.depositor_name} 입금 제외를 복구`}
              className={cn(ACTION_BASE, 'ml-auto shrink-0 px-3 font-medium text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50')}
              onClick={handleRestoreClick}
            >
              복구
            </button>
          )}

          {/* 제외(✗): 미확정·비제외 행에서 '주문 아님'으로 치움. picker 펼친 동안은 숨김(연결 흐름 방해 방지). */}
          {uiStatus !== 'confirmed' && uiStatus !== 'excluded' && onReject && !manualOpen && (
            <button
              type="button"
              aria-haspopup="dialog"
              aria-label={`${won(deposit.amount)} ${deposit.depositor_name} 입금을 주문 매칭에서 제외`}
              className={cn(ACTION_BASE, 'ml-auto shrink-0 px-3 font-medium text-slate-400 hover:bg-slate-50 hover:text-slate-600')}
              onClick={handleRejectClick}
            >
              제외
            </button>
          )}
        </div>

        {/* 주문 상세 인라인 (확신·불일치 행의 '상세' 토글). picker 펼친 동안은 숨김(중복 방지). */}
        {detailOpen && suggested && !manualOpen && renderOrderDetail(suggested)}

        {/* 애매(복수 후보): 후보 선택 + [확인] (메인 줄 아래 전체 폭).
            금액만 같은 후보 중에 실제 주문이 없을 수 있어 [다른 주문]으로 수동 picker 진입 허용. */}
        {uiStatus === 'ambiguous' && !manualOpen && (
          <div className="px-5 pb-4">
            <p className="mb-2 text-[13px] font-medium text-amber-800">
              일치 후보 {displayCandidates.length}건 — 매칭할 주문을 선택하세요
            </p>
            {/* 동일 캡: 후보가 짧으면 높이 그대로, 길어질 때만 내부 스크롤(수동 픽커와 동일 규격) */}
            <div className="max-h-80 overflow-y-auto overscroll-contain p-0.5">
              <CandidatePicker
                candidates={displayCandidates}
                selectedId={selectedId}
                onSelect={setSelectedId}
              />
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                aria-label={`${won(deposit.amount)} ${deposit.depositor_name} 입금을 다른 주문에 매칭`}
                className={cn(ACTION_BASE, 'px-3 font-medium text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50')}
                onClick={() => openManualLink()}
              >
                다른 주문
              </button>
              <button
                type="button"
                aria-haspopup="dialog"
                aria-label={`${won(deposit.amount)} ${deposit.depositor_name} 입금을 선택한 주문으로 확인`}
                className={cn(ACTION_BASE, 'bg-slate-900 px-4 text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40')}
                disabled={selectedId === null}
                onClick={handleConfirmClick}
              >
                확인
              </button>
            </div>
          </div>
        )}

        {/* 미매칭/amber/애매/확신 수동연결: 미지정 주문 picker(닉네임 검색 + 금액맞는 건 유력 정렬) → 선택 → [연결] */}
        {(uiStatus === 'unmatched' || uiStatus === 'amount_mismatch' || uiStatus === 'ambiguous' || isConfident) && manualOpen && (
          <div className="px-5 pb-4">
            <p className="mb-2 text-[13px] font-medium text-slate-700">
              매칭할 주문을 확인하고 선택하세요 (닉네임 검색 가능)
            </p>
            <input
              ref={manualInputRef}
              type="text"
              value={manualQuery}
              onChange={(e) => setManualQuery(e.target.value)}
              placeholder="닉네임 검색"
              aria-label="매칭할 주문 닉네임 검색"
              className="mb-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
            />
            {manualLoading ? (
              <p className="py-2 text-sm text-slate-400">불러오는 중…</p>
            ) : (manualCandidates ?? []).length === 0 ? (
              <p className="py-2 text-sm text-slate-400">기간 내 미지정 주문이 없습니다</p>
            ) : visibleManualCandidates.length === 0 ? (
              <p className="py-2 text-sm text-slate-400">검색 결과가 없습니다</p>
            ) : (
              /* 후보가 많으면(윈도우 내 미지정 전량) 행이 통째로 길어져 피로 — 5행 높이 캡 + 내부 스크롤
                 (오너 e2e 피드백 2026-07-25). overscroll-contain = 목록 끝에서 페이지 스크롤 전파 차단,
                 p-0.5 = 포커스 링 클리핑 방지. */
              <div className="max-h-80 overflow-y-auto overscroll-contain p-0.5">
                <CandidatePicker
                  candidates={visibleManualCandidates}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  nameMatchedIds={nameMatchedIds}
                  depositAmount={deposit.amount}
                />
              </div>
            )}
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                className={cn(ACTION_BASE, 'px-3 font-medium text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50')}
                onClick={closeManualLink}
              >
                취소
              </button>
              <button
                type="button"
                aria-haspopup="dialog"
                aria-label={`${won(deposit.amount)} ${deposit.depositor_name} 입금을 선택한 주문에 매칭`}
                className={cn(ACTION_BASE, 'bg-slate-900 px-4 text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40')}
                disabled={!manualSelectionVisible}
                onClick={() => {
                  // 보이는 목록에서만 선택(검색으로 가려진 주문 연결 차단). ConfirmGuard → onConfirm(=linkMatch) → 다리 학습.
                  const chosen = visibleManualCandidates.find((c) => c.id === selectedId);
                  if (chosen) openConfirmFor(chosen);
                }}
              >
                매칭
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmGuard
        open={guard !== null}
        title={guard?.title ?? ''}
        warning={guard?.mode === 'confirm' ? guard.warning : undefined}
        notice={guard?.mode === 'confirm' || guard?.mode === 'rescue' ? guard.notice : undefined}
        rows={guard?.mode === 'confirm' || guard?.mode === 'rescue' ? guard.rows : undefined}
        confirmLabel={
          guard?.mode === 'undo' ? '되돌림'
            : guard?.mode === 'reject' ? '제외'
              : guard?.mode === 'restore' ? '복구'
                : guard?.mode === 'rescue' ? '입금 완료 처리'
                  : '확정'
        }
        description={
          guard?.mode === 'reject' ? "'매칭 제외'에서 다시 복구할 수 있어요."
            : guard?.mode === 'restore' ? '다시 매칭 후보로 올라가요.'
              : guard?.mode === 'confirm' || guard?.mode === 'rescue' ? guard.description
                : undefined
        }
        tone={
          guard?.mode === 'confirm' || guard?.mode === 'rescue' ? 'positive'
            : guard?.mode === 'undo' ? 'caution'
              : guard?.mode === 'restore' ? 'info'
                : 'neutral'
        }
        subject={{
          amount: deposit.amount,
          // 은행 표시 생략(리스트와 통일 — 단일 연동계좌). 입금자만, 라벨 프리픽스로 명시.
          label: deposit.depositor_name ? `입금자 ${deposit.depositor_name}` : '',
        }}
        loading={loading}
        onConfirm={handleGuardConfirm}
        onCancel={() => {
          if (!loading) setGuard(null);
        }}
      />
    </>
  );
}
